<template>
  <SubNavigation
    :title="pageData.name"
    :children-links="pageData.parentNodePage?.children"
  />

  <Container
    class="mx-auto mt-10 mb-20 sm:mt-10 md:mt-15 md:mb-25 md:max-w-140 lg:max-w-195.5 xl:mt-17.5 xl:mb-30 xl:max-w-264 2xl:mt-20 2xl:mb-35"
  >
    <div
      class="flex w-full flex-col items-center justify-center gap-4 lg:flex-row lg:gap-5"
    >
      <div class="w-69.5 sm:w-91.5 md:w-88.75 lg:w-74.5 xl:w-87.5 2xl:w-102.75">
        <Selector
          v-model="selectedMonth"
          placeholder="Měsíc (vše)"
          :options="monthFilterOptions"
          class="my-0!"
        />
      </div>

      <div
        v-if="scheduleYear?.fileItem?.url"
        class="w-69.5 sm:w-91.5 md:w-88.75 lg:w-74.5 xl:w-87.5 2xl:w-102.75"
      >
        <NuxtLink
          :to="scheduleYear?.fileItem?.url"
          :download="scheduleYear?.fileItem?.fileName"
          target="_blank"
          class="flex w-full items-center justify-between bg-black p-2.5 text-white md:text-[17px] md:leading-[123%] xl:text-xl xl:leading-[120%] 2xl:p-3.75 2xl:text-[23px]/[29px]!"
        >
          <span>Stáhnout (PDF)</span>

          <div
            class="flex h-6.5 w-6.5 shrink-0 items-center justify-center rounded-full bg-white"
          >
            <DownloadIcon class="h-3.75 w-3.5 text-black" />
          </div>
        </NuxtLink>
      </div>
    </div>
  </Container>

  <Container
    class="mx-auto mb-10 md:mb-15 md:max-w-119.5 lg:max-w-195.5 xl:mb-17.5 xl:max-w-264 2xl:mb-20!"
  >
    <div
      class="flex flex-wrap items-center justify-center gap-2 sm:px-1 md:gap-2.5 md:px-0!"
    >
      <button
        v-for="tag in tags"
        :key="tag.id"
        @click="selectTag(tag.slug[locale])"
        class="group cursor-pointer"
      >
        <TypeBadge
          :text="tag.name"
          :class="[
            'text-xs! duration-300 ease-out group-hover:bg-black! group-hover:text-white! min-[1024px]:text-sm/[120%]! 2xl:text-[17px]/[130%]!',
            {
              'border-black! bg-black! text-white!': selectedTags.includes(
                tag.slug[locale],
              ),
            },
          ]"
        />
      </button>
    </div>
  </Container>

  <Container
    class="bp1680:w-[860px] mx-auto mb-30 min-h-59.5 w-full px-5 transition-opacity duration-300 md:w-[478px] md:px-0! lg:w-[616px] xl:mb-42.5 xl:w-[740px] 2xl:w-[860px]"
    :class="{ 'pointer-events-none opacity-40': isSchedulePending }"
  >
    <ScheduleList
      v-if="scheduleItems?.length > 0"
      :items="scheduleItems"
      :has-more="hasMoreItems"
      @load-more="loadMore"
    />

    <section v-else-if="!isSchedulePending" class="py-12 md:py-20">
      <p class="text-center text-2xl md:text-4xl xl:text-6xl">
        {{ t('common.no_schedues_found') }}
      </p>
    </section>
  </Container>
</template>

<script setup lang="ts">
import type { PageData, Schedule } from '~/types'

const props = defineProps<{ pageData: PageData }>()

useDynamicSeo(props.pageData)

const monthFilterOptions = [
  { name: { cs: 'Měsíc (vše)', en: 'Month (all)' }, slug: 'all' },
  { name: { cs: 'Leden', en: 'January' }, slug: '1' },
  { name: { cs: 'Únor', en: 'February' }, slug: '2' },
  { name: { cs: 'Březen', en: 'March' }, slug: '3' },
  { name: { cs: 'Duben', en: 'April' }, slug: '4' },
  { name: { cs: 'Květen', en: 'May' }, slug: '5' },
  { name: { cs: 'Červen', en: 'June' }, slug: '6' },
  { name: { cs: 'Červenec', en: 'July' }, slug: '7' },
  { name: { cs: 'Srpen', en: 'August' }, slug: '8' },
  { name: { cs: 'Září', en: 'September' }, slug: '9' },
  { name: { cs: 'Říjen', en: 'October' }, slug: '10' },
  { name: { cs: 'Listopad', en: 'November' }, slug: '11' },
  { name: { cs: 'Prosinec', en: 'December' }, slug: '12' },
]

const PER_PAGE = 18
const { locale, t } = useI18n()
const route = useRoute()
const router = useRouter()

const page = ref(Number(route.query.page) || 1)
const fetchFirst = computed(() => PER_PAGE * page.value)
const categorySlug = computed(() => route.path.split('/').at(-1))

const selectedMonth = ref(
  monthFilterOptions.find((opt) => opt.slug === route.query.month) ||
    monthFilterOptions[0],
)

const appliedMonth = ref(selectedMonth.value)

const selectedTags = ref<string[]>(
  (route.query.tag as string)?.split(',')?.filter(Boolean) || [],
)

const monthQueryValue = computed(() => {
  return appliedMonth.value?.slug === 'all'
    ? undefined
    : Number(appliedMonth.value?.slug)
})

const scheduleItems = ref<Schedule[]>([])
const hasMoreItems = ref(false)

const [
  { data: scheduleTags },
  { data: scheduleYearData },
  { data: scheduleList, pending: isSchedulePending },
] = await Promise.all([
  usePersistedFetch('ProgramTags'),
  usePersistedFetch('GetProgramCategoryBySlug', {
    slug: categorySlug,
  }),
  usePersistedFetch('GetPrograms', {
    categorySlug,
    first: fetchFirst,
    page: 1,
    tagSlugs: computed(() =>
      selectedTags.value.length ? selectedTags.value : undefined,
    ),
    month: monthQueryValue,
  }),
])

const tags = computed(() => scheduleTags.value?.getProgramTags || [])

const scheduleYear = computed(
  () => scheduleYearData.value?.getProgramCategoryBySlug,
)

const loadMore = () => {
  page.value++

  router.push({
    query: {
      ...route.query,
      page: page.value > 1 ? String(page.value) : undefined,
    },
  })
}

watch(
  () => scheduleList.value,
  (newValue: any) => {
    const responseData = newValue?.getPrograms || newValue
    scheduleItems.value = responseData?.data || []
    hasMoreItems.value = responseData?.paginatorInfo?.hasMorePages ?? false
  },
  { immediate: true },
)

let monthTimer: ReturnType<typeof setTimeout> | null = null

watch(selectedMonth, (newVal) => {
  if (monthTimer) clearTimeout(monthTimer)

  monthTimer = setTimeout(() => {
    page.value = 1
    appliedMonth.value = newVal

    router.push({
      query: {
        ...route.query,
        page: undefined,
        month: newVal?.slug === 'all' ? undefined : newVal?.slug,
      },
    })
  }, 300)
})

const selectTag = (tagValue: string) => {
  page.value = 1

  const index = selectedTags.value.indexOf(tagValue)
  if (index > -1) {
    selectedTags.value.splice(index, 1)
  } else {
    selectedTags.value.push(tagValue)
  }

  router.push({
    query: {
      ...route.query,
      page: undefined,
      tag: selectedTags.value?.length
        ? selectedTags.value.join(',')
        : undefined,
    },
  })
}

watch(
  () => route.query,
  (newQuery) => {
    const qPage = Number(newQuery.page) || 1
    if (qPage !== page.value) {
      page.value = qPage
    }

    const monthOpt =
      monthFilterOptions.find((opt) => opt.slug === newQuery.month) ||
      monthFilterOptions[0]

    if (selectedMonth.value?.slug !== monthOpt?.slug) {
      selectedMonth.value = monthOpt
      appliedMonth.value = monthOpt
    }

    selectedTags.value =
      (newQuery.tag as string)?.split(',')?.filter(Boolean) || []
  },
  { deep: true },
)
</script>
